import type { ReactNode } from 'react';
import { drawText, layoutText, type TextStyle } from '../../elements/flex_label.ts';
import { lastNumber } from '../../values/readings.ts';
import { CanvasView } from '../../view/canvas_view.tsx';
import { TILE_FONT } from '../../view/theme.ts';
import { useTileTextColor } from '../../view/tile_style.ts';
import { useTileEnvironment } from '../../view/tile_environment.ts';
import { TABLE_LINE_ALPHA, TABLE_TEXT_COLOR, tableCellRects, tableLines, tableSources, tableTextSize, tableTileContent, type TableTile } from './table_tile.ts';

/** The labels' weight with `boldFirstRow` (TT-2): the bundled Titillium Web Bold. */
const BOLD = 700;

/**
 * The content area of a `table` tile: the column labels and the rows of cells, all text at one size
 * (TT-1), with the divider lines the options ask for (TT-3).
 */
export function TableTileView({ tile }: { tile: TableTile }) : ReactNode {
    const environment = useTileEnvironment();
    const sources = tableSources(tile);
    const readings = environment.useReadingsList(tile, sources);
    const cellData = sources
        .map((source, index) => ({ source, value: lastNumber(readings[index]) }))
        .filter((cell) => cell.source !== undefined);
    const textColor = useTileTextColor(TABLE_TEXT_COLOR);
    const content = tableTileContent(tile, cellData, environment.locale, textColor);
    const { columns, boldFirstRow, rowLines, columnLines } = tile.options;
    const weights = columns.map((column) => column.width);

    return (
        <CanvasView
            deps={[JSON.stringify(content), boldFirstRow, rowLines, columnLines]}
            draw={(ctx, width, height) => {
                const rects = tableCellRects(weights, content.length, width, height);
                // Sized by the font's cap height and descent, not each text's ink, so that the rows line up.
                const styleOf = (isBold: boolean) : TextStyle => ({ ...TILE_FONT, ...(isBold ? { weight: BOLD } : {}) });
                const size = tableTextSize(content, rects, boldFirstRow, (text, box, isBold) => layoutText(ctx, text, box.width, box.height, styleOf(isBold)).size);

                for (const [row, cells] of content.entries()) {
                    for (const [column, cell] of cells.entries()) {
                        const box = rects[row]?.[column];
                        if (box && size !== undefined) {
                            drawText(ctx, cell.text, box, cell.color, { ...styleOf(boldFirstRow && row === 0), size });
                        }
                    }
                }

                const lines = tableLines(rects, rowLines, columnLines);
                if (lines.length > 0) {
                    // On whole device pixels, so that a line is sharp, not spread over two.
                    const scale = ctx.getTransform().a || 1;
                    const deviceWidth = Math.max(1, Math.round(scale));
                    const sharp = (at: number) : number => (Math.floor(at * scale) + (deviceWidth % 2) / 2) / scale;

                    ctx.save();
                    ctx.strokeStyle = textColor;
                    ctx.globalAlpha = TABLE_LINE_ALPHA;
                    ctx.lineWidth = deviceWidth / scale;
                    ctx.beginPath();
                    for (const line of lines) {
                        if ('y' in line) {
                            ctx.moveTo(0, sharp(line.y));
                            ctx.lineTo(width, sharp(line.y));
                        } else {
                            ctx.moveTo(sharp(line.x), 0);
                            ctx.lineTo(sharp(line.x), height);
                        }
                    }
                    ctx.stroke();
                    ctx.restore();
                }
            }}
        />
    );
}
